package dk.smark.demo.ui.theme

import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp

object SmarkColors {
    val Bg          = Color(0xFFF7F7F4)
    val Panel       = Color(0xFFFFFFFF)
    val Text        = Color(0xFF1F2933)
    val Muted       = Color(0xFF5F6B76)
    val Accent      = Color(0xFF256F5C)
    val AccentDark  = Color(0xFF184B3E)
    val Border      = Color(0xFFDFE4E2)

    // Derived / support colours
    val AccentLight = Color(0xFF3F9F86)   // used in brand-mark gradient
    val MapBg       = Color(0xFFEEF3EF)   // map-card background tint
    val TagBg       = Color(0xFFFBFCFB)   // .tag background
    val White       = Color(0xFFFFFFFF)
    val OnAccent    = Color(0xFFFFFFFF)   // text on accent-coloured surfaces
    val ShadowTint  = Color(0xFF1F2933)   // shadow base colour (use with alpha)
}

private val LightColorScheme = lightColorScheme(
    primary          = SmarkColors.Accent,
    onPrimary        = SmarkColors.OnAccent,
    primaryContainer = SmarkColors.AccentLight,
    onPrimaryContainer = SmarkColors.AccentDark,

    secondary        = SmarkColors.AccentDark,
    onSecondary      = SmarkColors.OnAccent,
    secondaryContainer = SmarkColors.MapBg,
    onSecondaryContainer = SmarkColors.Text,

    background       = SmarkColors.Bg,
    onBackground     = SmarkColors.Text,

    surface          = SmarkColors.Panel,
    onSurface        = SmarkColors.Text,
    surfaceVariant   = SmarkColors.Border,
    onSurfaceVariant = SmarkColors.Muted,

    outline          = SmarkColors.Border,
    outlineVariant   = SmarkColors.Border,
)

private val DarkColorScheme = darkColorScheme(
    primary          = SmarkColors.AccentLight,
    onPrimary        = SmarkColors.AccentDark,
    primaryContainer = SmarkColors.AccentDark,
    onPrimaryContainer = SmarkColors.AccentLight,

    secondary        = SmarkColors.Accent,
    onSecondary      = SmarkColors.OnAccent,
    secondaryContainer = SmarkColors.AccentDark,
    onSecondaryContainer = SmarkColors.AccentLight,

    background       = SmarkColors.Text,        // dark bg ≈ inverted text colour
    onBackground     = SmarkColors.Bg,

    surface          = Color(0xFF1A2430),        // slightly lighter than bg
    onSurface        = SmarkColors.Bg,
    surfaceVariant   = Color(0xFF2C3A44),
    onSurfaceVariant = SmarkColors.Muted,

    outline          = SmarkColors.Muted,
    outlineVariant   = SmarkColors.Border,
)

val LabradorGunmetalLightScheme = lightColorScheme(
    primary = Color(0xFFF5C55B),         // Labrador yellow
    onPrimary = Color(0xFF2B2F33),       // Gunmetal dark
    secondary = Color(0xFFE8A842),       // Muted amber
    onSecondary = Color(0xFF2B2F33),     // Gunmetal dark
    background = Color(0xFFF3F4F6),      // Pale gunmetal
    onBackground = Color(0xFF3C3F42),    // Mid-tone gunmetal
    surface = Color(0xFFF7F7F9),         // Off-white gunmetal tint
    onSurface = Color(0xFF2B2F33),       // Dark gunmetal
    surfaceVariant = Color(0xFFE0DED8),  // Warm grey/metal
    onSurfaceVariant = Color(0xFF504F4E),// Medium gunmetal
    error = Color(0xFFBA1A1A),
    onError = Color(0xFFFFFFFF)
)


val LabradorGunmetalDarkScheme = darkColorScheme(
    primary = Color(0xFFF5C55B),         // Labrador yellow
    onPrimary = Color(0xFF121415),       // Deep gunmetal
    secondary = Color(0xFFD8A44D),       // Rich golden amber
    onSecondary = Color(0xFF1C1E20),     // Charcoal gunmetal
    background = Color(0xFF1C1E20),      // Gunmetal base
    onBackground = Color(0xFFE9E1D3),    // Light yellow-cream
    surface = Color(0xFF232629),         // Darker gunmetal
    onSurface = Color(0xFFF8EBC6),       // Soft yellow glow
    surfaceVariant = Color(0xFF40464B),  // Steel tone
    onSurfaceVariant = Color(0xFFCCC2B8),
    error = Color(0xFFFFB4AB),
    onError = Color(0xFF690005)
)


// Define your typography
private val AppTypography = Typography(
    bodyLarge = TextStyle(
        fontWeight = FontWeight.Normal,
        fontSize = 16.sp,
        lineHeight = 24.sp,
    ),
    titleLarge = TextStyle(
        fontWeight = FontWeight.Bold,
        fontSize = 22.sp,
        lineHeight = 28.sp,
    ),
    labelSmall = TextStyle(
        fontWeight = FontWeight.Medium,
        fontSize = 11.sp,
        lineHeight = 16.sp,
    )
)

@Composable
fun AppTheme(
    darkTheme: Boolean = false,
    content: @Composable () -> Unit
) {
    val colorScheme = if (darkTheme) DarkColorScheme else LightColorScheme

    MaterialTheme(
        colorScheme = colorScheme,
        typography = AppTypography,
        content = content
    )
}